import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useFormBuilderWorkspace } from "@/features/forms/components/form-builder/formBuilderWorkspace";

export type AutoSaveStatus = "saved" | "unsaved" | "saving" | "error";

interface ActiveAutoSave {
    flush: () => Promise<void>;
    retry: () => void;
    status: () => AutoSaveStatus;
}

const activeAutoSaves = new Set<ActiveAutoSave>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export function getAutoSaveStatus(): AutoSaveStatus {
    const statuses = [...activeAutoSaves].map((save) => save.status());
    if (statuses.includes("error")) return "error";
    if (statuses.includes("saving")) return "saving";
    if (statuses.includes("unsaved")) return "unsaved";
    return "saved";
}

export function useAutoSaveStatus() {
    return useSyncExternalStore(
        (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getAutoSaveStatus,
        () => "saved"
    );
}

export async function flushAutoSaves() {
    await Promise.all([...activeAutoSaves].map((save) => save.flush()));
    if (getAutoSaveStatus() === "error") throw new Error("Resolve failed draft saves before continuing.");
}

export function retryAutoSaves() {
    [...activeAutoSaves].forEach((save) => save.retry());
}

export function useBuilderAutoSave<T>(value: T, save: (value: T) => Promise<unknown>) {
    const workspace = useFormBuilderWorkspace();
    return useAutoSave(value, save, workspace ? 0 : 600);
}

export function useAutoSave<T>(value: T, save: (value: T) => Promise<unknown>, delayMs = 600) {
    const [status, setStatus] = useState<AutoSaveStatus>("saved");
    const statusRef = useRef<AutoSaveStatus>("saved");
    const [error, setError] = useState<unknown>(null);
    const firstRender = useRef(true);
    const mounted = useRef(true);
    const latestValue = useRef(value);
    const latestSave = useRef(save);
    const version = useRef(0);
    const queue = useRef<Promise<void>>(Promise.resolve());

    latestValue.current = value;
    latestSave.current = save;

    const updateStatus = useCallback((next: AutoSaveStatus) => {
        statusRef.current = next;
        setStatus(next);
        notify();
    }, []);

    const enqueue = useCallback(
        (nextValue: T) => {
            const saveVersion = ++version.current;
            if (mounted.current) updateStatus("saving");
            queue.current = queue.current
                .catch(() => undefined)
                .then(() => latestSave.current(nextValue))
                .then(() => {
                    if (mounted.current && saveVersion === version.current) {
                        setError(null);
                        updateStatus("saved");
                    }
                })
                .catch((caught) => {
                    if (mounted.current && saveVersion === version.current) {
                        setError(caught);
                        updateStatus("error");
                    }
                });
        },
        [updateStatus]
    );

    const debouncedSave = useDebouncedCallback(enqueue, delayMs);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        updateStatus("unsaved");
        if (delayMs <= 0) enqueue(value);
        else debouncedSave(value);
    }, [debouncedSave, delayMs, enqueue, updateStatus, value]);

    const flush = useCallback(() => {
        debouncedSave.flush();
        return queue.current;
    }, [debouncedSave]);

    const retry = useCallback(() => enqueue(latestValue.current), [enqueue]);

    useEffect(() => {
        mounted.current = true;
        const active = { flush, retry, status: () => statusRef.current };
        activeAutoSaves.add(active);
        notify();
        return () => {
            const pending = flush();
            mounted.current = false;
            void pending.finally(() => {
                activeAutoSaves.delete(active);
                notify();
            });
        };
    }, [flush, retry]);

    return {
        status,
        error,
        retry,
        flush,
    };
}
